import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { AdminPageHeader } from './AdminUi';
import './admin.css';

export default function AdminDoctorReviews() {
  const { doctorId } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [data, setData] = useState({ total: 0, results: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [d, r] = await Promise.all([
        api.get(`admin/doctors/${doctorId}/`),
        api.get('admin/ratings/', { params: { doctor_id: doctorId, limit: 200 } }),
      ]);
      setDoctor(d.data);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);

  const remove = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await api.delete(`admin/ratings/${id}/`);
      toast.success('Review deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Delete failed');
    }
  };

  const avg = Number(doctor?.average_rating || 0).toFixed(1);
  // Prefer live ratings API total — Doctor.total_reviews can be stale
  const reviewCount = data.total ?? doctor?.total_reviews ?? 0;

  return (
    <div>
      <AdminPageHeader
        title={doctor ? `${doctor.name} — Reviews` : 'Doctor Reviews'}
        subtitle={
          doctor
            ? `★ ${avg} out of 5 · ${reviewCount} reviews`
            : 'Loading…'
        }
      >
        <Link to={`/admin/doctors/${doctorId}`} className="btn btn-outline-secondary rounded-pill px-3">
          ← Back to profile
        </Link>
      </AdminPageHeader>

      {doctor && (
        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <div className="admin-card p-3 text-center h-100">
              <div className="fw-bold fs-4 mb-0 text-warning">★ {avg}</div>
              <div className="small text-muted">Average rating out of 5</div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="admin-card p-3 text-center h-100">
              <div className="fw-bold fs-4 mb-0">{reviewCount}</div>
              <div className="small text-muted">Total reviews</div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="admin-card p-3 text-center h-100">
              <div className="fw-bold fs-4 mb-0">{doctor.specialization || '—'}</div>
              <div className="small text-muted">Specialization</div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : data.results.length === 0 ? (
        <div className="admin-card p-4 text-center text-muted">No reviews yet for this doctor.</div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {data.results.map((r) => (
            <div className="admin-card p-3 p-md-4" key={r.id}>
              <div className="d-flex gap-3 align-items-start">
                <div className="admin-person-avatar flex-shrink-0">
                  {r.user_image ? (
                    <img src={r.user_image} alt="" />
                  ) : (
                    (r.user_name || r.user_email || 'U').slice(0, 1).toUpperCase()
                  )}
                </div>
                <div className="flex-grow-1 min-w-0">
                  <div className="d-flex flex-wrap justify-content-between gap-2 mb-1">
                    <div>
                      <div className="fw-semibold">{r.user_name || r.user_email || 'Patient'}</div>
                      <div className="small text-muted">{r.user_email}</div>
                    </div>
                    <div className="text-end">
                      <div className="text-warning fw-bold">
                        {'★'.repeat(r.rating)}
                        <span className="text-muted fw-normal">{'☆'.repeat(Math.max(0, 5 - r.rating))}</span>
                      </div>
                      <div className="small text-muted">
                        {r.rating}/5
                        {r.created_at ? ` · ${new Date(r.created_at).toLocaleString()}` : ''}
                      </div>
                    </div>
                  </div>
                  <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                    {r.review_text || <span className="text-muted">No written review</span>}
                  </p>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {r.appointment_id && (
                      <span className="small text-muted">Appointment #{r.appointment_id}</span>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger rounded-pill px-3 ms-auto"
                      onClick={() => remove(r.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
